import { selectCustomerDetails } from './customer'
import { selectIsCbmExpandEnabled } from './order'
import { lobTitles } from '../constants/orderservices'
import { appTypes } from '../constants/session'
import { orderRoutes } from '../constants/navigation'
import { selectActivePageId } from './router'
import { CBM_ELIGIBLE_LOBS_BASE, CBM_ELIGIBLE_LOBS_EXPANDED } from '../constants/configuration'
import { cbmStatusTypes, cbmEligibilityMessages } from '../constants/order'

export const selectSelectedFilters = state => {
  const { filters: lobFilters } = state.offers
  return Object.values(lobFilters).reduce(
    (acc, { filterProperty, filter, filters, excludeWhenEmpty }) => {
      // get selected filters (including child filters)
      const selected = (filters || [filter])
        .flatMap(f => f.filters || f)
        .reduce(
          (all, f) =>
            f.isSelected
              ? [
                  ...all,
                  { [filterProperty]: f.id || f.id === 0 ? f.id : f.value }
                ]
              : all,
          []
        )
      const shouldInclude = selected.length || !excludeWhenEmpty

      return {
        ...acc,
        ...(shouldInclude && { [filterProperty]: selected })
      }
    },
    {}
  )
}

export const selectShouldLoadMoreOffers = ({ offers }) => {
  const { currentPage, totalPages } = offers.request.metadata

  return currentPage < totalPages
}

export const selectVisibleFilters = state => {
  const { filters: lobFilters } = state.offers
  const { site } = selectCustomerDetails(state)
  const { appType } = state.session
  const {
    homeBasedBusiness,
    canAgentSellSmartOffice,
    serviceabilityMessage,
    nonServiceableLobs,
    smartOfficeLicenceMessage
  } = site
  const { searchTerm, contractTerm, ...filters } = lobFilters

  const isHomeBasedBusinessTv = lob =>
    homeBasedBusiness && lob === lobFilters.tv.id

  const isNotSmartOfficeLicensed = lob =>
    !canAgentSellSmartOffice && lob === lobFilters.smartoffice?.id

  const isNotCBMStandaloneEligible = lob => {
    if(lob === lobFilters.mobile?.id) {
      // Always hide mobile filter - no longer showing in UI
      return true
    }

    return false
  }

  function getVisibilityMessage(lob) {
    const { hbbServiceability, nonServiceableLob } = serviceabilityMessage || {}

    if (isHomeBasedBusinessTv(lob)) {
      return {
        title: 'HBB',
        message: hbbServiceability?.peoMessage
      }
    }

    if (
      !homeBasedBusiness &&
      !canAgentSellSmartOffice &&
      nonServiceableLobs?.length === 0 &&
      smartOfficeLicenceMessage &&
      lob === lobFilters?.smartoffice?.id &&
      appType === appTypes.NC
    ) {
      return {
        message: smartOfficeLicenceMessage?.peoMessage
      }
    }

    if (!homeBasedBusiness && (nonServiceableLobs || []).length) {
      const hasLob = nonServiceableLobs.find(nLob => nLob === lobTitles[lob])

      if (!hasLob) {
        return null
      }

      return {
        title: `Not Serviceable for ${lobTitles[lob]}`,
        message: nonServiceableLob?.peoMessage.replace(
          '{LOB}',
          lobTitles[lob].replace('Business ', '')
        )
      }
    }

    return null
  }

  return Object.entries(filters).reduce(
    (all, [lob, filter]) => ({
      ...all,
      [lob]: {
        ...filter,
        isVisible: !(
          isHomeBasedBusinessTv(lob) || isNotSmartOfficeLicensed(lob) ||
          isNotCBMStandaloneEligible(lob)
        ),
        visibilityMessage: getVisibilityMessage(lob)
      }
    }),
    { searchTerm, contractTerm }
  )
}

export const selectHasExistingOfferPackage = existingServices => {
  return (
    !!existingServices?.offerPackage &&
    !!Object.keys(existingServices?.offerPackage).length
  )
}

export const selectOfferPackageItems = (existingServices = {}) => {
  const { cartItems, isPackageOffer } = existingServices

  if (!cartItems) {
    return []
  }

  return cartItems
    .reduce((packagedItems, { cartItemDetails = [], offer = {} }) => {
      const items = cartItemDetails.reduce((includedPackageItems, item) => {
        return item.isIncludedInPackage
          ? [...includedPackageItems, item]
          : includedPackageItems
      }, packagedItems)

      if (isPackageOffer) {
        return items
      }

      return offer.isIncludedInPackage ? [...items, offer] : items
    }, [])
    .sort((a, b) => {
      if (a.type === 'offer' && b.type !== 'offer') {
        return -1
      }
      if (b.type === 'offer') {
        return 1
      }
      return 0
    })
}

export const selectHasInternetFiltersSelected = state => {
  const { filters } = state.offers
  const internetGroup = filters.internet
  if (!internetGroup || !internetGroup.filters) {
    return false
  }
  return internetGroup.filters.some(filter => filter.isSelected)
}
// Returns true if any CBM-eligible LOB filter (BI, BV, or BTV) is selected

export const selectHasCbmEligibleFiltersSelected = state => {
  const { filters } = state.offers
  const isCbmExpandEnabled = selectIsCbmExpandEnabled(state)
  const cbmEligibleLobs = isCbmExpandEnabled
    ? CBM_ELIGIBLE_LOBS_EXPANDED
    : CBM_ELIGIBLE_LOBS_BASE
  const eligibleGroups = cbmEligibleLobs.map(lob => filters[lob])
  return eligibleGroups.some(group => {
    if (!group?.filters) return false
    return group.filters
      .flatMap(f => f.filters || f)
      .some(f => f.isSelected)
  })
}

export const selectCbmEligibilityInfo = state => {
  const pageId = selectActivePageId(state)
  const {
    configure: configureRoute,
    offers: offersRoute,
    documents: documentsRoute
  } = orderRoutes

  const hasCbmEligibleFiltersSelected =
    selectHasCbmEligibleFiltersSelected(state)
  const isCbmExpandEnabled = selectIsCbmExpandEnabled(state)
  const cbmEligibleLobs = isCbmExpandEnabled
    ? CBM_ELIGIBLE_LOBS_EXPANDED
    : CBM_ELIGIBLE_LOBS_BASE
  const { cbmStatus } = state.order || {}
  const { account } = state.customer?.details || {}
  const isTaxExempt = account?.taxExempt || false
  const isHidden = !cbmStatus || cbmStatus === cbmStatusTypes.hasCbm
    || cbmStatus === cbmStatusTypes.notEligible
  const isDisabled = cbmStatus === cbmStatusTypes.taxExemptEligible ||
    isTaxExempt
  
  if (isHidden) {
    return {
      isAvailable: false,
      message: cbmEligibilityMessages.notEligibleCriteria
    }
  }
  
  if (isDisabled) {
    return {
      isAvailable: false,
      message: cbmEligibilityMessages.taxExemptNotEligible
    }
  }
  
  if (pageId === offersRoute.id) {
    if (!hasCbmEligibleFiltersSelected) {
      return {
        isAvailable: false,
        message: isCbmExpandEnabled
          ? cbmEligibilityMessages.needsEligibleLobOnOffers
          : cbmEligibilityMessages.needsInternetOnOffers
      }
    }
    return {
      isAvailable: true,
      message: null
    }
  }
  
  if (pageId === configureRoute.id) {
    const { lineOfBusinesses = [] } = state.configuration?.offer || {}
    const hasEligibleLobInOffer = lineOfBusinesses.some(
      lob => cbmEligibleLobs.includes(lob)
    )
    const hasEligibleLobConfig = cbmEligibleLobs.some(
      lob => !!state.configuration?.[lob]
    )
    const isLobAvailable = hasEligibleLobInOffer || hasEligibleLobConfig ||
      hasCbmEligibleFiltersSelected

    if (!isLobAvailable) {
      return {
        isAvailable: false,
        message: isCbmExpandEnabled
          ? cbmEligibilityMessages.needsEligibleLobOnConfigure
          : cbmEligibilityMessages.needsInternetOnConfigure
      }
    }
    
    return {
      isAvailable: true,
      message: null
    }
  }
  
  if (pageId === documentsRoute.id) {
    const cartItems = state.cart?.details?.cartItems || []
    const hasCbmEligibleLobInCart = cartItems.some(
      item => cbmEligibleLobs.includes(item.lineOfBusiness)
    )

    if (!hasCbmEligibleLobInCart) {
      return {
        isAvailable: false,
        message: isCbmExpandEnabled
          ? cbmEligibilityMessages.needsEligibleLobOnDocuments
          : cbmEligibilityMessages.needsInternetOnDocuments
      }
    }

    return {
      isAvailable: true,
      message: null
    }
  }
  
  if (!hasCbmEligibleFiltersSelected) {
    return {
      isAvailable: false,
      message: cbmEligibilityMessages.selectFiltersToEnable
    }
  }
  
  return {
    isAvailable: true,
    message: null
  }
}
